'use client';
import JarvisCard from '@components/card/jarvis-card';
import { Suspense, useState } from 'react';
import usePersistentURL from '@hooks/use-persistent-url';
import useGetDocPabeans from './apis/use-cases/use-get-doc-pabeans';
import SearchInput, { type OnSubmitHandler } from '@components/search';
import DocPabeanTable from '@components/doc-pabean/doc-pabean-table';
import { Loader, Text, Select, Modal, Stack, Group, Button, Switch } from '@mantine/core';
import { IconDownload } from '@tabler/icons-react';
import DownloadReportModal from '@components/doc-pabean/DownloadReportModal';
import DocPabeanCreditsBanner from '@components/doc-pabean/DocPabeanCreditsBanner';
import { ErrorBoundary } from 'react-error-boundary';
import { useQueryErrorResetBoundary } from '@tanstack/react-query';
import { useTheme } from '@contexts/ThemeContext';
import { useLanguage } from '@contexts/LanguageContext';
import { JarvisPrimaryButton, JarvisSecondaryButton, JarvisInputLabel } from '@components/jarvis-ui';
import { JarvisDatePicker } from '@components/jarvis-ui/JarvisDatePicker';
import axiosInstance from '@libs/axios';
import { notifications } from '@mantine/notifications';
import dayjs from 'dayjs';
import { BRANCH_ALL } from '@constants/branch';
import useGetBranchNames from './apis/use-cases/use-get-branch-names';

type DocPabeanTableSectionProps = {
  currentPage: number;
  pageSize: number;
  searchQuery: string;
  statusFilter: string;
  orderBy: string;
  ascending: string;
  includeDeleted: boolean;
  branchFilter: string;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  onSearch: OnSubmitHandler;
};

function DocPabeanTableSection({
  currentPage,
  pageSize,
  searchQuery,
  statusFilter,
  orderBy,
  ascending,
  includeDeleted,
  branchFilter,
  onPageChange,
  onPageSizeChange,
  onSearch,
}: DocPabeanTableSectionProps) {
  const { data: queryResult, isFetching } = useGetDocPabeans({
    page: currentPage,
    itemPerPage: pageSize,
    searchQuery,
    statusFilter,
    orderBy,
    ascending,
    includeDeleted,
    branchFilter,
  });

  const totalPage = pageSize > 0 ? Math.ceil(queryResult.totalCount / pageSize) : 0;

  return (
    <DocPabeanTable
      data={queryResult.data}
      numberPageSize={pageSize}
      numberPage={currentPage}
      loading={isFetching}
      setNumberPageSize={onPageSizeChange}
      onSearch={onSearch}
      totalPage={totalPage}
      onPageChange={onPageChange}
    />
  );
}

function TableFallback() {
  return (
    <div className="flex items-center justify-center py-16">
      <Loader size="lg" />
    </div>
  );
}

function TableErrorFallback({
  error,
  onRetry,
}: {
  error: Error;
  onRetry: () => void;
}) {
  const { t } = useLanguage();
  return (
    <div className="flex flex-col items-center gap-4 py-16">
      <Text size="sm" c="red" ta="center">
        {error.message || t('docpabean.errorLoad')}
      </Text>
      <JarvisSecondaryButton onClick={onRetry}>{t('docpabean.retry')}</JarvisSecondaryButton>
    </div>
  );
}

export default function DocPabeanPage() {
  const { mode, colors: themeColors } = useTheme();
  const { t } = useLanguage();
  const {
    currentPage,
    searchQuery,
    pageSize,
    statusFilter,
    sortBy,
    branchFilter,
    onPageChange,
    onSearchQueryChange,
    onPageSizeChange,
    onStatusFilterChange,
    onSortByChange,
    onBranchFilterChange,
  } = usePersistentURL();

  const { branches, hasMultipleBranches } = useGetBranchNames();

  // Off by default: the list shows live documents exactly as before. Turning
  // it on surfaces rows deleted in Jarvis, whose CEISA filing still exists.
  const [includeDeleted, setIncludeDeleted] = useState(false);

  const [downloadModalOpened, setDownloadModalOpened] = useState(false);
  const [usageModalOpened, setUsageModalOpened] = useState(false);
  const [usageDownloading, setUsageDownloading] = useState(false);
  const [usageStartDate, setUsageStartDate] = useState<Date | null>(null);
  const [usageEndDate, setUsageEndDate] = useState<Date | null>(null);
  const [usageDocType, setUsageDocType] = useState<string | null>(null);
  const { reset } = useQueryErrorResetBoundary();

  const handleDownloadUsageReport = async () => {
    if (!usageStartDate || !usageEndDate) {
      notifications.show({ title: t('docpabean.errorTitle'), message: t('docpabean.errSelectDates'), color: 'red' });
      return;
    }
    setUsageDownloading(true);
    try {
      const params = new URLSearchParams();
      if (usageDocType) params.append('docType', usageDocType);
      const queryString = params.toString();
      const response = await axiosInstance.post(
        `/api/v1/dashboard/usage-dashboard-detail${queryString ? `?${queryString}` : ''}`,
        {
          startDate: dayjs(usageStartDate).format('YYYY-MM-DD'),
          endDate: dayjs(usageEndDate).format('YYYY-MM-DD'),
        },
        { responseType: 'blob' }
      );
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `usage_dashboard_${dayjs(usageStartDate).format('YYYY-MM-DD')}_${dayjs(usageEndDate).format('YYYY-MM-DD')}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      notifications.show({ title: 'Success', message: t('docpabean.successDownloaded'), color: 'green' });
      setUsageModalOpened(false);
    } catch (error) {
      console.error('Error downloading usage report:', error);
      notifications.show({ title: t('docpabean.errorTitle'), message: t('docpabean.errDownload'), color: 'red' });
    } finally {
      setUsageDownloading(false);
    }
  };

  const safeCurrentPage = currentPage > 0 ? currentPage : 1;
  const safePageSize = pageSize > 0 ? pageSize : 10;

  const statusOptions = [
    { value: '', label: t('docpabean.allStatus') },
    { value: 'BILLING', label: 'BILLING' },
    { value: 'SPPB', label: 'SPPB' },
    { value: 'SPTNP', label: 'SPTNP' },
    { value: 'BILLING SPTNP', label: 'BILLING SPTNP' },
    { value: 'NPE', label: 'NPE' },
    { value: '1', label: '1' },
    { value: 'UMUM', label: 'UMUM' },
    { value: 'SPJM', label: 'SPJM' },
  ];

  // '' is a real choice, not an absence: it sends no branch param, which the
  // API reads as "the caller's own branch".
  const branchOptions = [
    { value: '', label: t('docpabean.branchMine') },
    { value: BRANCH_ALL, label: t('docpabean.branchAll') },
    ...branches.map((branch) => ({ value: branch, label: branch })),
  ];

  // A single-office tenant has no filter to render, so ignore any branch left
  // in the URL — otherwise a link shared from a multi-branch tenant would
  // silently narrow their list with no visible control to undo it.
  const effectiveBranchFilter = hasMultipleBranches ? branchFilter : '';

  const sortOptions = [
    { value: '', label: t('docpabean.sortNewest') },
    { value: 'ajuNo:asc', label: t('docpabean.sortAjuAsc') },
    { value: 'ajuNo:desc', label: t('docpabean.sortAjuDesc') },
    { value: 'namaPerusahaan:asc', label: t('docpabean.sortShipperAsc') },
    { value: 'namaPerusahaan:desc', label: t('docpabean.sortShipperDesc') },
    { value: 'tanggalDaftar:asc', label: t('docpabean.sortDateOldest') },
    { value: 'tanggalDaftar:desc', label: t('docpabean.sortDateNewest') },
    { value: 'totalItem:asc', label: t('docpabean.sortItemLeast') },
    { value: 'totalItem:desc', label: t('docpabean.sortItemMost') },
  ];

  const [sortField, sortDirection] = sortBy.split(':');
  const orderBy = sortField || 'id';
  const ascending = sortDirection === 'asc' ? 'true' : 'false';

  const handleSearch: OnSubmitHandler = (value) => {
    // onSearchQueryChange now handles resetting page to 1 internally
    onSearchQueryChange(value);
  };

  const handleSortChange = (value: string | null) => {
    // onSortByChange handles resetting page to 1 internally
    onSortByChange(value || '');
  };

  const handleStatusChange = (value: string | null) => {
    // onStatusFilterChange now handles resetting page to 1 internally
    onStatusFilterChange(value || '');
  };

  const handleBranchChange = (value: string | null) => {
    // Not clearable: '' is the default option ("Cabang Saya"), not an absence.
    // onBranchFilterChange handles resetting page to 1 internally.
    onBranchFilterChange(value ?? '');
  };

  const handlePageChange = (newPage: number) => {
    onPageChange(Math.max(1, newPage));
  };

  const handlePageSizeChange = (value: number) => {
    onPageSizeChange(value);
  };

  return (
    <div data-testid="doc-pabean-page">
      <JarvisCard
        style={{
          padding: 24,
          backgroundColor: themeColors.card,
          border: `1px solid ${themeColors.border}`,
          borderRadius: '8px',
        }}
      >
        <DocPabeanCreditsBanner />
        <div className="mb-4 flex justify-between items-center gap-3">
          <div className="flex gap-3 flex-1" data-tour="docpabean-filters">
            <div className="w-[30%]">
              <SearchInput onSubmit={handleSearch} defaultValue={searchQuery} />
            </div>
            <div className="w-[20%]">
              <Select
                placeholder={t('docpabean.sortPlaceholder')}
                value={sortBy}
                onChange={handleSortChange}
                data={sortOptions}
                clearable
                styles={{
                  input: {
                    backgroundColor: themeColors.formFields,
                    color: themeColors.text,
                    borderColor: themeColors.border,
                    height: '40px',
                  },
                  dropdown: {
                    backgroundColor: themeColors.card,
                    borderColor: themeColors.border,
                  },
                  option: {
                    color: themeColors.text,
                    '&[data-selected]': {
                      backgroundColor: `${themeColors.primary}20`,
                      color: themeColors.primary,
                    },
                    '&[data-hovered]': {
                      backgroundColor: `${themeColors.primary}10`,
                    },
                  },
                }}
              />
            </div>
            <div className="w-[20%]">
              <Select
                placeholder={t('docpabean.filterStatus')}
                value={statusFilter}
                onChange={handleStatusChange}
                data={statusOptions}
                clearable
                styles={{
                  input: {
                    backgroundColor: themeColors.formFields,
                    color: themeColors.text,
                    borderColor: themeColors.border,
                    height: '40px',
                  },
                  dropdown: {
                    backgroundColor: themeColors.card,
                    borderColor: themeColors.border,
                  },
                  option: {
                    color: themeColors.text,
                    '&[data-selected]': {
                      backgroundColor: `${themeColors.primary}20`,
                      color: themeColors.primary,
                    },
                    '&[data-hovered]': {
                      backgroundColor: `${themeColors.primary}10`,
                    },
                  },
                }}
              />
            </div>
            {hasMultipleBranches && (
              <div className="w-[20%]">
                <Select
                  placeholder={t('docpabean.filterBranch')}
                  value={branchFilter}
                  onChange={handleBranchChange}
                  data={branchOptions}
                  allowDeselect={false}
                  styles={{
                    input: {
                      backgroundColor: themeColors.formFields,
                      color: themeColors.text,
                      borderColor: themeColors.border,
                      height: '40px',
                    },
                    dropdown: {
                      backgroundColor: themeColors.card,
                      borderColor: themeColors.border,
                    },
                    option: {
                      color: themeColors.text,
                      '&[data-selected]': {
                        backgroundColor: `${themeColors.primary}20`,
                        color: themeColors.primary,
                      },
                      '&[data-hovered]': {
                        backgroundColor: `${themeColors.primary}10`,
                      },
                    },
                  }}
                />
              </div>
            )}
          </div>
          <div className="flex gap-2 items-center">
            <Switch
              size="sm"
              checked={includeDeleted}
              onChange={(event) => setIncludeDeleted(event.currentTarget.checked)}
              label={t('docpabean.showDeleted')}
              styles={{ label: { color: themeColors.text, whiteSpace: 'nowrap' } }}
            />
            <JarvisSecondaryButton onClick={() => setUsageModalOpened(true)} leftIcon={<IconDownload size={16} />}>
              {t('docpabean.downloadUsage')}
            </JarvisSecondaryButton>
            <JarvisPrimaryButton
              data-tour="docpabean-report"
              onClick={() => setDownloadModalOpened(true)}
              leftIcon={<IconDownload size={16} />}
            >
              {t('docpabean.downloadReport')}
            </JarvisPrimaryButton>
          </div>
        </div>
        <ErrorBoundary
          onReset={reset}
          resetKeys={[
            safeCurrentPage,
            safePageSize,
            searchQuery,
            statusFilter,
            sortBy,
            includeDeleted,
            effectiveBranchFilter,
          ]}
          fallbackRender={({ error, resetErrorBoundary }) => (
            <TableErrorFallback error={error as Error} onRetry={resetErrorBoundary} />
          )}
        >
          <Suspense fallback={<TableFallback />}>
            <div data-tour="docpabean-table">
              <DocPabeanTableSection
                currentPage={safeCurrentPage}
                pageSize={safePageSize}
                searchQuery={searchQuery}
                statusFilter={statusFilter}
                orderBy={orderBy}
                ascending={ascending}
                includeDeleted={includeDeleted}
                branchFilter={effectiveBranchFilter}
                onPageChange={handlePageChange}
                onPageSizeChange={(value) => {
                  handlePageSizeChange(value);
                }}
                onSearch={handleSearch}
              />
            </div>
          </Suspense>
        </ErrorBoundary>
      </JarvisCard>

      <DownloadReportModal
        opened={downloadModalOpened}
        onClose={() => setDownloadModalOpened(false)}
        branchFilter={effectiveBranchFilter}
      />

      <Modal
        opened={usageModalOpened}
        onClose={() => setUsageModalOpened(false)}
        title={
          <Group gap="xs">
            <IconDownload size={18} style={{ color: themeColors.text }} />
            <Text size="md" fw={600} style={{ color: themeColors.text }}>
              {t('docpabean.downloadUsage')}
            </Text>
          </Group>
        }
        size="sm"
        centered
        styles={{
          content: { background: themeColors.card, border: `1px solid ${themeColors.border}` },
          header: { borderBottom: `1px solid ${themeColors.border}`, backgroundColor: themeColors.card },
          body: { padding: '1.5rem', backgroundColor: themeColors.card },
        }}
      >
        <Stack gap="md">
          <div>
            <JarvisInputLabel required>{t('docpabean.startDate')}</JarvisInputLabel>
            <JarvisDatePicker
              placeholder={t('docpabean.selectStartDate')}
              valueFormat="DD-MM-YYYY"
              value={usageStartDate}
              onChange={(val) => setUsageStartDate(val)}
              clearable
            />
          </div>
          <div>
            <JarvisInputLabel required>{t('docpabean.endDate')}</JarvisInputLabel>
            <JarvisDatePicker
              placeholder={t('docpabean.selectEndDate')}
              valueFormat="DD-MM-YYYY"
              value={usageEndDate}
              onChange={(val) => setUsageEndDate(val)}
              clearable
              minDate={usageStartDate || undefined}
            />
          </div>
          <div>
            <JarvisInputLabel>{t('docpabean.docType')}</JarvisInputLabel>
            <Select
              placeholder={t('docpabean.all')}
              data={[
                { value: 'PIB', label: 'PIB' },
                { value: 'PEB', label: 'PEB' },
              ]}
              value={usageDocType}
              onChange={setUsageDocType}
              clearable
              styles={{
                input: { backgroundColor: themeColors.card, color: themeColors.text, borderColor: themeColors.border },
                dropdown: { backgroundColor: themeColors.card, borderColor: themeColors.border },
              }}
            />
          </div>
          <Group justify="flex-end" gap="sm" pt="md">
            <Button
              variant="outline"
              onClick={() => setUsageModalOpened(false)}
              disabled={usageDownloading}
              styles={{ root: { borderColor: themeColors.border, color: themeColors.text } }}
            >
              {t('docpabean.cancel')}
            </Button>
            <Button
              onClick={handleDownloadUsageReport}
              loading={usageDownloading}
              disabled={!usageStartDate || !usageEndDate}
              leftSection={<IconDownload size={16} />}
              styles={{ root: { backgroundColor: themeColors.primary, color: themeColors.buttonText } }}
            >
              {t('docpabean.download')}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </div>
  );
}
